import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/context/LanguageContext';
import { restaurantInfo } from '@/data/menuData';
import LanguageSwitcher from './LanguageSwitcher';
import ReservationModal from './ReservationModal';
import CampaignPopup from './CampaignPopup';
import { ShoppingBag, CalendarCheck, UtensilsCrossed, Construction } from 'lucide-react';
import { toast } from 'sonner';
import { trackEvent, trackOnce } from '@/lib/analytics';

const WelcomeScreenOriginal = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [isReservationOpen, setIsReservationOpen] = useState(false);
  const [showCampaign, setShowCampaign] = useState(false);

  useEffect(() => {
    // Track page visit (once per session)
    trackOnce('page_visit');
    // Show campaign popup once per session
    const campaignShown = sessionStorage.getItem('campaign-shown');
    if (!campaignShown) {
      const timer = setTimeout(() => {
        setShowCampaign(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleCloseCampaign = () => {
    setShowCampaign(false);
    sessionStorage.setItem('campaign-shown', 'true');
  };

  const texts = {
    tr: {
      welcome: 'Hoş Geldiniz',
      viewMenu: 'Menüye Git',
      onlineOrder: 'Online Sipariş',
      reservation: 'Rezervasyon',
      underConstruction: 'Yapım Aşamasında',
      comingSoon: 'Online sipariş sistemi çok yakında hizmetinizde olacak!',
    },
    en: {
      welcome: 'Welcome',
      viewMenu: 'View Menu',
      onlineOrder: 'Online Order',
      reservation: 'Reservation',
      underConstruction: 'Under Construction',
      comingSoon: 'Online ordering system will be available very soon!',
    },
  };

  const t = texts[language];

  const handleOnlineOrder = () => {
    window.open(restaurantInfo.onlineOrderUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-between relative overflow-hidden">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ 
          backgroundImage: `url(${restaurantInfo.backgroundImage})`,
        }}
      />
      
      {/* Dark Overlay for readability */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black/80" />

      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
        <div className="absolute top-10 left-10 w-32 h-32 bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-48 h-48 bg-secondary/10 rounded-full blur-3xl" />
      </div>

      {/* Language Switcher */}
      <div className="w-full p-4 flex justify-start z-10">
        <LanguageSwitcher />
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 text-center z-10">
        {/* Logo */}
        <div className="relative mb-6 animate-fade-in-up">
          <div className="w-36 h-36 sm:w-44 sm:h-44 rounded-full bg-gradient-to-br from-charcoal-light/90 to-charcoal/90 backdrop-blur-sm border-2 border-primary/40 flex items-center justify-center shadow-gold-lg relative overflow-hidden">
            {/* Inner glow */}
            <div className="absolute inset-0 bg-gradient-to-t from-primary/15 to-transparent" />
            {/* Logo text */}
            <div className="relative z-10 text-center">
              <span className="font-display text-3xl sm:text-4xl text-gradient-gold tracking-wider">
                gomşu
              </span>
            </div>
          </div>
          {/* Outer ring decoration */}
          <div className="absolute -inset-3 border border-primary/30 rounded-full animate-pulse-gold" />
        </div>

        {/* Restaurant Name */}
        <h1 className="font-display text-3xl sm:text-4xl text-foreground mb-2 tracking-wide animate-fade-in-up stagger-1 drop-shadow-lg">
          {restaurantInfo.name}
        </h1>

        {/* Welcome Text */}
        <p className="text-primary font-body text-lg sm:text-xl mb-8 animate-fade-in-up stagger-2 drop-shadow-md">
          {t.welcome}
        </p>

        {/* Action Buttons */}
        <div className="flex flex-col gap-3 w-full max-w-xs animate-fade-in-up stagger-3">
          {/* View Menu Button - Primary */}
          <Button
            onClick={() => { trackEvent('menu_click'); navigate('/menu'); }}
            className="w-full py-6 text-lg font-body font-medium gradient-gold text-primary-foreground rounded-full shadow-gold-lg hover:shadow-gold transition-all duration-300 hover:scale-105 flex items-center justify-center gap-2"
          >
            <UtensilsCrossed className="w-5 h-5" />
            {t.viewMenu}
          </Button>

          {/* Secondary Buttons Row */}
          <div className="grid grid-cols-2 gap-3">
            {/* Online Order Button */}
            <Button
              onClick={handleOnlineOrder}
              variant="outline"
              className="py-5 text-sm font-body font-medium bg-background/20 backdrop-blur-sm border-primary/50 text-foreground hover:bg-primary/20 hover:border-primary rounded-full transition-all duration-300 flex items-center justify-center gap-2"
            >
              <ShoppingBag className="w-4 h-4 text-primary" />
              <span className="truncate">{t.onlineOrder}</span>
            </Button>

            {/* Reservation Button */}
            <Button
              onClick={() => { trackEvent('reservation_click'); setIsReservationOpen(true); }}
              variant="outline"
              className="py-5 text-sm font-body font-medium bg-background/20 backdrop-blur-sm border-secondary/50 text-foreground hover:bg-secondary/20 hover:border-secondary rounded-full transition-all duration-300 flex items-center justify-center gap-2"
            >
              <CalendarCheck className="w-4 h-4 text-secondary" />
              <span className="truncate">{t.reservation}</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="w-full p-6 text-center z-10 animate-fade-in-up stagger-4">
        <p className="text-primary font-display text-sm tracking-wider drop-shadow-md">
          {restaurantInfo.name}
        </p>
      </div>

      {/* Reservation Modal */}
      <ReservationModal 
        isOpen={isReservationOpen} 
        onClose={() => setIsReservationOpen(false)} 
      />

      {/* Campaign Popup */}
      {showCampaign && (
        <CampaignPopup onClose={handleCloseCampaign} />
      )}
    </div>
  );
};

export default WelcomeScreenOriginal;
